import { DashboardContainer } from "components"
import React, { useState } from "react"

import { Link } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"

import { Select } from "antd"
import { ColumnsType } from "antd/es/table"
import { surveyTypes } from "../Survey"
import CustomInfoCard from "components/new-stuff/ui/CustomInfoCard"
import DeleteSurvey from "../modals/DeleteSurvey"
import { ICustomSurvey } from "../interface/templates"
import {
  ADMIN_PEOPLE_ENDPOINTS,
  adminPeopleService,
} from "services/adminPeopleService"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"

const Templates: React.FC<{}> = () => {
  const queryClient = useQueryClient()
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [activeDeleteId, setActiveDeleteId] = useState("")
  const [activeTab, setActiveTab] = useState("Custom")
  const [typeId, setTypeId] = useState("1")

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [ADMIN_PEOPLE_ENDPOINTS.SELECT_CUSTOM_SURVEY_TEMPLATES, typeId],
    })
    queryClient.invalidateQueries({
      queryKey: [
        ADMIN_PEOPLE_ENDPOINTS.SELECT_DEFAULT_SURVEY_TEMPLATES,
        typeId,
      ],
    })
  }

  const customQuery = useBackendTableQuery<IPaginatedRes<ICustomSurvey>>({
    queryKeyBase: [
      ADMIN_PEOPLE_ENDPOINTS.SELECT_CUSTOM_SURVEY_TEMPLATES,
      typeId,
    ],
    queryFn: (params) =>
      adminPeopleService.selectCustomSurveyTemplates({ typeId, ...params }),
    getTotal: (data) => data?.data?.totalDocs ?? 0,
    searchParamName: "searchParams",
    initialPageSize: 20,
  })

  const defaultQuery = useBackendTableQuery<IPaginatedRes<ICustomSurvey>>({
    queryKeyBase: [
      ADMIN_PEOPLE_ENDPOINTS.SELECT_DEFAULT_SURVEY_TEMPLATES,
      typeId,
    ],
    queryFn: (params) =>
      adminPeopleService.selectDefaultSurveyTemplates({ typeId, ...params }),
    getTotal: (data) => data?.data?.totalDocs ?? 0,
    searchParamName: "searchParams",
    initialPageSize: 20,
  })

  const data = activeTab === "Custom" ? customQuery : defaultQuery

  const TableColumns: ColumnsType<ICustomSurvey> = [
    {
      title: "Title",
      dataIndex: "description",
      render: (_, record) => (
        <span
          className={`whitespace-nowrap  font-avenir text-sm text-[#42526D]
               `}
        >
          {record.description}
        </span>
      ),
    },

    {
      title: "Status",
      dataIndex: "survey_template_status",
      render: (_, record) => (
        <span
          className={`text-sm font-medium py-1 block text-center w-[80px] rounded-[4px]
                    ${
                      record.survey_template_status_id === 2
                        ? "bg-success100 text-success800"
                        : ""
                    }
                    ${
                      record.survey_template_status_id === 1
                        ? "bg-neutral40 text-neutral500"
                        : ""
                    }
                    ${
                      record.survey_template_status_id === 3
                        ? "bg-[#FEE3B1] text-[#E69702]"
                        : ""
                    }

                    `}
        >
          {record.survey_template_status}
        </span>
      ),
    },

    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <TableActionSelect
          items={actionItems({
            id: record.survey_template_id,
            key: record.survey_template_key,
            statusId: record.survey_template_status_id,
          })}
        />
      ),
    },
  ]
  //  three dots on each row
  const actionItems = (data: { key: string; id: number; statusId: number }) => {
    let arrayOptions: any = []
    {
      data?.statusId === 2 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "0",
            label: (
              <Link to={`/dashboard/survey/template/preview?x=${data?.key}`}>
                Preview
              </Link>
            ),
          },
        ])
    }
    {
      data?.statusId === 2 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "1",
            label: (
              <Link to={`/dashboard/survey/template?x=${data?.key}`}>Edit</Link>
            ),
          },
        ])
    }

    {
      data?.statusId !== 2 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "2",
            label: (
              <Link to={`/dashboard/survey/template?x=${data?.key}`}>
                Finish
              </Link>
            ),
          },
        ])
    }
    {
      data?.statusId &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "3",
            label: (
              <div
                onClick={() => {
                  handleOpenDeleteModal(String(data.id))
                }}
              >
                Delete
              </div>
            ),
          },
        ])
    }

    return arrayOptions
  }
  const handleOpenDeleteModal = (id: string) => {
    setOpenDeleteConfirmation(true)
    setActiveDeleteId(String(id))
  }

  return (
    <DashboardContainer>
      <div className="relative">
        <CustomInfoCard
          title={
            <div className="border-b flex gap-2">
              {["Custom", "Default"].map((tab, index) => (
                <button
                  key={index}
                  className={`relative py-2 px-3 text-neutral500 font-semibold font-avenir text-base ${
                    activeTab === tab &&
                    "border-primary500 border-b-2 !text-primary500"
                  }`}
                  onClick={() => setActiveTab(tab as string)}
                >
                  {tab}
                </button>
              ))}
            </div>
          }
          actions={
            <Select
              defaultValue={typeId}
              style={{ width: 125 }}
              placeholder="Filter by"
              onChange={(value: string) => {
                setTypeId(value)
              }}
              options={surveyTypes.map((surv) => ({
                value: surv.survey_id,
                label: surv.survey_title,
              }))}
              className="font-avenir "
            />
          }
        >
          <div className=" mt-4 flex flex-col gap-4 px-6">
            <AppTable
              scroll={false}
              columns={TableColumns}
              dataSource={data.data?.data?.docs || []}
              rowKey="survey_template_id"
              loading={data.isLoading || data.isPreviousData}
              className="text-[#42526D]"
              pagination={data.tablePagination}
            />
          </div>
        </CustomInfoCard>
        <DeleteSurvey
          title="Delete Template"
          description="
          If you delete this template,  it would be removed from your list. Are you sure you want to continue?
          "
          deleteFn={adminPeopleService.removeSurveyTemplate}
          activeDeleteId={activeDeleteId}
          open={openDeleteConfirmation}
          onClose={() => {
            setOpenDeleteConfirmation(false)
            setActiveDeleteId("")
          }}
          invalidateQueries={invalidateQueries}
        />
      </div>
    </DashboardContainer>
  )
}
export default Templates
